<template>
  <v-card class="cx-card pa-5">
    <div class="d-flex align-center mb-1">
      <div>
        <div class="cx-card__title">
          <v-icon small color="#7b5cd6" class="mr-1">mdi-cart-check</v-icon>
          Checkout-Extension
        </div>
        <div class="cx-card__sub">Artwork, Trust-Box & Rahmen-Upsell im Plus-Checkout — Steuerung + A/B-Ergebnisse</div>
      </div>
      <v-spacer />
      <span v-if="config.testMode" class="cx-chip cx-chip--test">🔒 TEST-MODUS</span>
      <span v-if="config.abTest" class="cx-chip cx-chip--live ml-2">A/B-TEST LÄUFT</span>
      <span v-else class="cx-chip ml-2">A/B aus</span>
    </div>

    <!-- ── Einstellungen (Shop-Metafield, wirkt sofort im Checkout) ── -->
    <div class="cx-settings">
      <div v-for="opt in options" :key="opt.key" class="cx-setting">
        <div class="cx-setting__text">
          <strong>{{ opt.label }}</strong>
          <small>{{ opt.hint }}</small>
        </div>
        <v-switch
          :input-value="config[opt.key]"
          :disabled="saving"
          dense
          hide-details
          inset
          class="mt-0 pt-0"
          @change="(v) => saveConfig(opt.key, !!v)"
        />
      </div>
    </div>
    <div v-if="configError" class="cx-error">{{ configError }}</div>

    <v-divider class="my-4" />

    <!-- ── A/B-Report ── -->
    <div class="d-flex align-center mb-2">
      <div class="cx-card__title" style="font-size: 14px">A/B-Ergebnisse</div>
      <v-spacer />
      <input v-model="since" type="date" class="cx-date" :disabled="loadingReport" />
      <v-btn
        small
        rounded
        depressed
        color="#7b5cd6"
        dark
        class="ml-2"
        :loading="loadingReport"
        @click="loadReport"
      >
        <v-icon x-small left>mdi-refresh</v-icon> Laden
      </v-btn>
    </div>

    <template v-if="report">
      <div class="cx-kpis" v-if="report.test">
        <div class="cx-kpi">
          <span class="cx-kpi__label">Attach-Rate A → B</span>
          <span class="cx-kpi__value">
            {{ pct(report.test.attachA) }} → {{ pct(report.test.attachB) }}
            <small :class="report.test.deltaPp >= 0 ? 'pos' : 'neg'">
              {{ report.test.deltaPp >= 0 ? "+" : "" }}{{ report.test.deltaPp.toFixed(2) }} pp
            </small>
          </span>
        </div>
        <div class="cx-kpi">
          <span class="cx-kpi__label">AOV A → B</span>
          <span class="cx-kpi__value">
            {{ eur(report.test.aovA) }} → {{ eur(report.test.aovB) }}
            <small :class="report.test.aovDelta >= 0 ? 'pos' : 'neg'">
              {{ report.test.aovDelta >= 0 ? "+" : "" }}{{ report.test.aovDelta.toFixed(2) }} €
            </small>
          </span>
        </div>
        <div class="cx-kpi">
          <span class="cx-kpi__label">Signifikanz</span>
          <span class="cx-kpi__value">
            <span :class="report.test.significant ? 'pos' : ''">
              {{ report.test.significant ? "✓ signifikant" : "noch nicht" }}
            </span>
            <small>z = {{ report.test.z.toFixed(2) }}</small>
          </span>
        </div>
      </div>
      <div v-else class="cx-hint">
        Für den Signifikanz-Test braucht es &gt;30 Orders je Gruppe — Gruppen unten.
      </div>

      <table class="cx-table">
        <thead>
          <tr>
            <th>Gruppe</th><th>Orders</th><th>Umsatz</th><th>AOV</th>
            <th>mit Rahmen</th><th>Checkout-Adds</th><th>Rahmen-Umsatz</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(g, name) in report.groups" :key="name">
            <td>
              <strong>{{ groupName(name) }}</strong>
            </td>
            <td>{{ g.n }}</td>
            <td>{{ eur(g.rev) }}</td>
            <td>{{ eur(g.n ? g.rev / g.n : 0) }}</td>
            <td>{{ g.attach }} ({{ pct(g.n ? g.attach / g.n : 0) }})</td>
            <td>{{ g.cxUpsell }}</td>
            <td>{{ eur(g.frameRev) }}</td>
          </tr>
        </tbody>
      </table>
      <div class="cx-hint mt-1">
        {{ report.totalOrders }} bezahlte Orders seit {{ report.since }} ·
        „Kontrolle A" sieht nichts, „Variante B" den vollen Block ·
        Order-Zahl A vs. B ≈ Conversion-Effekt (50/50-Zuweisung)
      </div>
    </template>
    <div v-else-if="reportError" class="cx-error">{{ reportError }}</div>
  </v-card>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions as fbFunctions } from "../../../fb";

export default {
  name: "CheckoutTestCard",
  data() {
    return {
      config: { upsell: true, trustBox: true, abTest: false, alwaysShowArtwork: false, upsellDiscount: false },
      options: [
        { key: "testMode", label: "🔒 Test-Modus", hint: "Blöcke nur für dich sichtbar (deine E-Mail im Checkout ODER Rabattcode CXTEST) — Kunden sehen nichts" },
        { key: "upsell", label: "Rahmen-Upsell", hint: "Ein-Klick-Rahmen unter der Position" },
        { key: "upsellDiscount", label: "5-€-Angebot", hint: "„Jetzt hinzufügen & 5 € sparen“ — Code RAHMEN5 wird automatisch angewendet" },
        { key: "trustBox", label: "Trust-Box", hint: "„Warum du dir sicher sein kannst“ über dem Kauf-Button" },
        { key: "abTest", label: "A/B-Test (50/50)", hint: "Kontrollgruppe A sieht nichts — Basis für den Plus-Vergleich" },
        { key: "alwaysShowArtwork", label: "Artwork immer zeigen", hint: "Auch Gruppe A sieht das Artwork (Test misst dann nur den Upsell)" },
      ],
      saving: false,
      configError: "",
      since: new Date(Date.now() - 28 * 864e5).toISOString().slice(0, 10),
      report: null,
      loadingReport: false,
      reportError: "",
    };
  },
  created() {
    this.loadConfig();
  },
  methods: {
    pct(v) {
      return (100 * (v || 0)).toFixed(1) + " %";
    },
    eur(v) {
      return (v || 0).toLocaleString("de-DE", { maximumFractionDigits: 0 }) + " €";
    },
    groupName(name) {
      if (name === "A") return "Kontrolle A";
      if (name === "B") return "Variante B";
      return "ohne Zuweisung";
    },
    async loadConfig() {
      try {
        const call = httpsCallable(fbFunctions, "checkoutConfig");
        const { data } = await call({ action: "get" });
        if (data?.ok) this.config = data.config;
      } catch (e) {
        this.configError = "Config konnte nicht geladen werden: " + (e.message || e);
      }
    },
    async saveConfig(key, value) {
      this.saving = true;
      this.configError = "";
      const previous = { ...this.config };
      this.config = { ...this.config, [key]: value };
      try {
        const call = httpsCallable(fbFunctions, "checkoutConfig");
        const { data } = await call({ action: "set", config: { [key]: value } });
        if (data?.ok) this.config = data.config;
      } catch (e) {
        this.config = previous;
        this.configError = "Speichern fehlgeschlagen: " + (e.message || e);
      } finally {
        this.saving = false;
      }
    },
    async loadReport() {
      this.loadingReport = true;
      this.reportError = "";
      try {
        const call = httpsCallable(fbFunctions, "checkoutAbReport", { timeout: 300000 });
        const { data } = await call({ since: this.since });
        if (data?.ok) this.report = data;
        else this.reportError = "Report fehlgeschlagen";
      } catch (e) {
        this.reportError = "Report fehlgeschlagen: " + (e.message || e);
      } finally {
        this.loadingReport = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cx-card {
  border-radius: 14px !important;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07) !important;

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.8);
  }
  &__sub {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
  }
}

.cx-chip {
  padding: 3px 12px;
  border-radius: 999px;
  background: #f1f0f5;
  color: rgba(0, 0, 0, 0.55);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;

  &--live {
    background: #e8f5e9;
    color: #2e7d32;
  }
  &--test {
    background: #fff3e0;
    color: #b26a00;
  }
}

.cx-settings {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 24px;
  margin-top: 10px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}
.cx-setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #faf9fc;

  &__text {
    display: flex;
    flex-direction: column;
    strong {
      font-size: 13px;
      color: rgba(0, 0, 0, 0.75);
    }
    small {
      font-size: 11px;
      color: rgba(0, 0, 0, 0.45);
    }
  }
}

.cx-date {
  height: 30px;
  padding: 0 10px;
  border-radius: 8px;
  background: #f4f3f7;
  border: none;
  outline: none;
  font-size: 12px;
}

.cx-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
}
.cx-kpi {
  padding: 10px 14px;
  border-radius: 12px;
  background: #faf9fc;
  display: flex;
  flex-direction: column;

  &__label {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 700;
  }
  &__value {
    font-size: 16px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.8);

    small {
      margin-left: 6px;
      font-size: 12px;
    }
  }
}
.pos {
  color: #2e7d32;
}
.neg {
  color: #c62828;
}

.cx-table {
  width: 100%;
  border-collapse: collapse;

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(0, 0, 0, 0.45);
    text-align: left;
    padding: 6px 10px;
    border-bottom: 1px solid #eeecf4;
  }
  td {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
    padding: 7px 10px;
    border-bottom: 1px solid #f5f4f9;
  }
}

.cx-hint {
  font-size: 11.5px;
  color: rgba(0, 0, 0, 0.45);
}
.cx-error {
  margin-top: 8px;
  font-size: 12px;
  color: #c62828;
}
</style>
